import { AppData, Colors, Lightning } from '@lightningjs/sdk';
import SettingsToggleSection from './SettingsToggleSection';
import Button from 'components/common/buttons/Button';
import { convertAnchorTagsToBold } from 'support/textUtils';
import { translate } from 'support/translate';
import ToggleContent from './contents/ToggleContent';

export interface DataCenterSectionTemplateSpec
  extends Lightning.Component.TemplateSpec {
  Content: {
    DataCenterContent: typeof ToggleContent;
    DoNotSellToggle: typeof SettingsToggleSection;
    OneTrustButton: typeof Button;
  };
}

const BUTTON_WIDTH = 300;
const BUTTON_HEIGHT = 65;
const BUTTON_FONT_SIZE = 18;
const BUTTON_RADIUS = 5;
const BUTTON_STROKE = 2;

export default class DataCenterSection
  extends Lightning.Component<DataCenterSectionTemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<DataCenterSectionTemplateSpec>
{
  private _Content = this.getByRef('Content')!;
  private _DataCenterContent = this._Content.getByRef('DataCenterContent')!;
  private _DoNotSellToggle = this._Content.getByRef('DoNotSellToggle')!;
  private _DoNotSellToggleButtons =
    this._DoNotSellToggle.getByRef('ToggleButtons')!;
  private _OneTrustButton = this._Content.getByRef('OneTrustButton')!;

  get title() {
    const dataCenterContent = this._DataCenterContent.title;
    return [...dataCenterContent];
  }
  private get isOneTrustEnabled() {
    return AppData!.oneTrustService?.isInitialized ?? false;
  }

  static override _template(): Lightning.Component.Template<DataCenterSectionTemplateSpec> {
    return {
      Content: {
        flex: { direction: 'column' },
        DataCenterContent: { type: ToggleContent },
        DoNotSellToggle: {
          type: SettingsToggleSection,
          selectedIndex: this.isDoNotSell() ? 0 : 1,
          signals: {
            onChanged: '_onDnsPreferenceChanged',
          },
        },
        OneTrustButton: {
          type: Button,
          visible: false,
          height: BUTTON_HEIGHT,
          minWidth: BUTTON_WIDTH,
          label: translate('settings.DataCenter.managePrivacyChoicesButton'),
          action: '$managePrivacyChoices',
          signals: {
            $managePrivacyChoices: '$managePrivacyChoices',
          },
          fontSize: BUTTON_FONT_SIZE,
          radius: BUTTON_RADIUS,
          shader: {
            type: Lightning.shaders.RoundedRectangle,
            stroke: BUTTON_STROKE,
            strokeColor: Colors('white').get(),
            radius: BUTTON_RADIUS,
          },
          backgroundColor: {
            focused: Colors('white').get(),
            unfocused: Colors('alternateBackground').get(),
          },
          fontColor: {
            focused: Colors('black').get(),
            unfocused: Colors('text').get(),
          },
        },
      },
    };
  }

  override _active() {
    const isOneTrustEnabled = this.isOneTrustEnabled;
    this._DoNotSellToggle.patch({ visible: !isOneTrustEnabled });
    this._OneTrustButton.patch({ visible: isOneTrustEnabled });

    const privacyContent =
      AppData!.config?.login.settings.data_center.privacy_choices;
    if (!privacyContent) return;

    const privacyChoicesText = this.isOneTrustEnabled
      ? privacyContent.your_privacy_choices_ctv
      : privacyContent.your_privacy_choices_unsupported_one_trust_ctv;

    const rawDescriptionText = privacyChoicesText ?? '';
    const descriptionText = convertAnchorTagsToBold(rawDescriptionText);

    this._DataCenterContent.heading = privacyContent.title ?? '';
    this._DataCenterContent.description = descriptionText;
  }

  override _getFocused() {
    return (
      this.isOneTrustEnabled
        ? this._OneTrustButton
        : this._DoNotSellToggleButtons
    ) as Lightning.Component;
  }

  private static isDoNotSell() {
    return AppData!.privacyService.getDoNotSell();
  }

  private _onDnsPreferenceChanged(dns: boolean) {
    // The toggle stays attached while hidden, and its `_setup()` signals
    // `onChanged`. Ignore it so the hidden control can't write consent.
    if (this.isOneTrustEnabled) return;

    AppData!.privacyService.setDoNotSell(dns);
  }

  private $managePrivacyChoices() {
    AppData?.log.debug('[OneTrust] Manage Privacy Choices pressed');
    AppData?.oneTrustService?.showPreferenceCenter();
  }
}
